import React, { Component } from 'react'
import { Alert, Badge, Button, Card, Empty, Input, Modal, Radio, Result, Select, Space, Spin, Table, Tag, Typography, message } from 'antd'
import { BulbOutlined, CheckCircleOutlined, CloudServerOutlined, DatabaseOutlined, ReloadOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons'
import { withRouter } from 'react-router-dom'
import {
    applyVibeProjectPermission,
    approveVibeProjectPermissionRequest,
    getAllVibeProjectList,
    getMyVibeProjectPermissionRequestCount,
    getMyVibeProjectPermissionRequestList,
    getVibeProjectAuthorization,
    recommendVibeProjects,
    saveVibeProjectAuthorization,
} from '../../request'
import {
    buildDatasetAuthorizationGrants,
    getDatasetPolicyMeta,
    isSystemManagedDatasetGrant,
} from './vibeDataset'
import {
    DEFAULT_PROJECT_KIND_FILTER,
    filterVibeProjects,
    getVibeProjectKind,
} from './vibeProjectFilter'
import VibeCodexGuideDialog from '../vibe/VibeCodexGuideDialog'
import { VIBE_CODEX_GUIDE_SCENE } from '../vibe/vibeCodexGuide'

const { Text } = Typography

const DEPLOYMENT_MODE_META = {
    server: { text: '服务器部署', color: 'blue' },
    local: { text: '仅本地运行', color: 'default' },
}

const PROJECT_KIND_META = {
    application: { text: '业务应用', color: 'blue' },
    dataset: { text: '共享数据集', color: 'cyan' },
}

const BUSINESS_SUBJECT_META = {
    lechun: { text: '乐纯', color: 'green' },
    superbody: { text: '超体', color: 'orange' },
}

const STATUS_META = {
    approved: { text: '已创建', color: 'green' },
    provisioning: { text: '接入中', color: 'processing' },
    ready: { text: '已就绪', color: 'green' },
    failed: { text: '接入失败', color: 'red' },
    disabled: { text: '已停用', color: 'default' },
}

const UPDATE_FREQUENCY_META = {
    daily: '每日',
    weekly: '每周',
    monthly: '每月',
    quarterly: '每季度',
    irregular: '不定期',
}

class VibeProjectList extends Component {
    state = {
        loading: true,
        loadError: false,
        projects: [],
        projectFilter: 'all',
        projectKindFilter: DEFAULT_PROJECT_KIND_FILTER,
        aiRequirement: '',
        aiLoading: false,
        aiResult: null,
        actionProjectId: null,
        actionType: null,
        batchApplyVisible: false,
        batchApplyProjects: [],
        selectedBatchProjectIds: [],
        batchApplying: false,
        codexGuideVisible: false,
        codexGuideScene: VIBE_CODEX_GUIDE_SCENE.NORMAL,
        authorizationVisible: false,
        authorizationLoading: false,
        authorizationLoadError: false,
        authorizationSaving: false,
        authorizationProjectId: null,
        authorizationProject: null,
        authorizationUsers: [],
        selectedAuthorizationUserIds: [],
        authorizationKeyword: '',
        pendingRequestCount: 0,
        requestCountLoading: false,
        requestVisible: false,
        requestLoading: false,
        requestLoadError: false,
        permissionRequests: [],
        approvingRequestId: null,
        selectedRequestIds: [],
        batchApproving: false,
        focusedRequestId: null,
    }

    componentDidMount() {
        const authorizeProjectId = this.getLinkedAuthorizationProjectId()
        this.loadProjects().then(projects => {
            if (!authorizeProjectId) return
            const project = projects.find(item => String(item.id) === String(authorizeProjectId))
            this.clearLinkedAuthorizationProjectId()
            if (project && this.isTrue(project.isOwner)) {
                this.openAuthorization(project)
            }
        })
        this.loadPendingRequestCount()
        const requestId = this.getLinkedRequestId()
        if (requestId) {
            this.setState({
                requestVisible: true,
                focusedRequestId: requestId,
            }, this.loadPermissionRequests)
        }
    }

    getLinkedRequestId = () => {
        const search = new URLSearchParams(this.props.location?.search)
        return search.get('requestId')
    }

    getLinkedAuthorizationProjectId = () => {
        const search = new URLSearchParams(this.props.location?.search)
        return search.get('authorizeProjectId')
    }

    clearLinkedAuthorizationProjectId = () => {
        const { history, location } = this.props
        const search = new URLSearchParams(location?.search)
        search.delete('authorizeProjectId')
        history.replace({
            pathname: location.pathname,
            search: search.toString(),
        })
    }

    loadProjects = () => {
        this.setState({ loading: true, loadError: false })
        return getAllVibeProjectList({})
            .then(projects => {
                const latestProjects = Array.isArray(projects) ? projects : []
                const projectById = new Map(latestProjects.map(project => [String(project.id), project]))
                this.setState(prevState => ({
                    projects: latestProjects,
                    loading: false,
                    aiResult: prevState.aiResult ? {
                        ...prevState.aiResult,
                        recommendations: (prevState.aiResult.recommendations || []).map(recommendation => ({
                            ...recommendation,
                            ...(projectById.get(String(recommendation.id)) || {}),
                        })),
                        } : null,
                }))
                return latestProjects
            })
            .catch(() => {
                this.setState({ loading: false, loadError: true })
                return []
            })
    }

    loadPendingRequestCount = () => {
        this.setState({ requestCountLoading: true })
        getMyVibeProjectPermissionRequestCount({})
            .then(res => this.setState({
                pendingRequestCount: Number(res?.pendingCount) || 0,
            }))
            .catch(() => {})
            .finally(() => this.setState({ requestCountLoading: false }))
    }

    refreshPage = () => {
        this.loadProjects()
        this.loadPendingRequestCount()
    }

    recommendProjects = () => {
        const requirement = this.state.aiRequirement.trim()
        if (!requirement) {
            message.warning('请先描述你需要什么数据')
            return
        }
        this.setState({ aiLoading: true, aiResult: null })
        recommendVibeProjects({ requirement })
            .then(result => this.setState({
                aiResult: {
                    answer: result?.answer || '',
                    recommendations: Array.isArray(result?.recommendations) ? result.recommendations : [],
                },
            }))
            .catch(() => {})
            .finally(() => this.setState({ aiLoading: false }))
    }

    isTrue = value => value === true || value === 1 || value === '1' || value === 'true'

    getUpdateFrequencyText = record => {
        try {
            const config = typeof record.updateFrequencyConfig === 'string'
                ? JSON.parse(record.updateFrequencyConfig)
                : record.updateFrequencyConfig
            if (config?.sourceText) return config.sourceText
        } catch (error) {
            // Fall back to the normalized frequency when legacy config is not valid JSON.
        }
        return UPDATE_FREQUENCY_META[record.updateFrequency] || record.updateFrequency || '-'
    }

    isDatasetProject = project => getVibeProjectKind(project) === 'dataset'

    filterProjects = (projects, projectFilter, projectKindFilter) => (
        filterVibeProjects(projects, projectFilter, projectKindFilter)
    )

    getEmptyDescription = (projectFilter, projectKindFilter) => {
        const kindText = projectKindFilter === 'dataset'
            ? '共享数据集'
            : (projectKindFilter === 'application' ? '业务应用' : 'Vibe 项目')
        if (projectFilter === 'owned') return `暂无我创建的${kindText}`
        if (projectFilter === 'permitted') return `暂无我有权限的别人${kindText}`
        return `暂无${kindText}`
    }

    formatDate = value => {
        if (!value) return '-'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return String(value)
        const pad = number => (number < 10 ? `0${number}` : String(number))
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    renderStatus = status => {
        const meta = STATUS_META[(status || '').toLowerCase()] || { text: status || '-', color: 'blue' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderDeploymentMode = mode => {
        const meta = DEPLOYMENT_MODE_META[mode] || { text: mode || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderProjectKind = kind => {
        const meta = PROJECT_KIND_META[kind] || PROJECT_KIND_META.application
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderBusinessSubject = subject => {
        const meta = BUSINESS_SUBJECT_META[subject] || { text: subject || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    openAuthorization = record => {
        const projectId = record.id
        this.setState({
            actionProjectId: projectId,
            actionType: 'authorize',
            authorizationVisible: true,
            authorizationLoading: true,
            authorizationLoadError: false,
            authorizationProjectId: projectId,
            authorizationProject: record,
            authorizationUsers: [],
            selectedAuthorizationUserIds: [],
            authorizationKeyword: '',
        })
        getVibeProjectAuthorization({ projectId })
            .then(res => {
                const users = Array.isArray(res?.accounts) ? res.accounts : []
                const authorizationProject = { ...record, ...(res || {}) }
                this.setState({
                    authorizationProject,
                    authorizationUsers: users,
                    selectedAuthorizationUserIds: users
                        .filter(item => (
                            this.isTrue(item.authorized)
                            || this.isTrue(item.isOwner)
                            || (this.isDatasetProject(authorizationProject)
                                && isSystemManagedDatasetGrant(item, true))
                        ))
                        .map(item => String(item.userId)),
                })
            })
            .catch(() => this.setState({ authorizationLoadError: true }))
            .finally(() => this.setState({
                actionProjectId: null,
                actionType: null,
                authorizationLoading: false,
            }))
    }

    saveAuthorization = () => {
        const {
            authorizationProjectId,
            authorizationProject,
            authorizationUsers,
            selectedAuthorizationUserIds,
        } = this.state
        if (!authorizationProjectId || this.state.authorizationLoading || this.state.authorizationLoadError) return
        this.setState({ authorizationSaving: true })
        saveVibeProjectAuthorization({
            projectId: authorizationProjectId,
            grants: buildDatasetAuthorizationGrants(
                selectedAuthorizationUserIds,
                authorizationUsers,
                this.isDatasetProject(authorizationProject),
            ),
        })
            .then(() => {
                message.success('授权保存成功')
                this.setState({
                    authorizationVisible: false,
                    authorizationProjectId: null,
                    authorizationProject: null,
                    authorizationUsers: [],
                    selectedAuthorizationUserIds: [],
                    authorizationKeyword: '',
                })
                this.loadProjects()
            })
            .catch(() => {})
            .finally(() => this.setState({ authorizationSaving: false }))
    }

    applyProjectPermission = projectId => {
        this.setState({ actionProjectId: projectId, actionType: 'apply' })
        applyVibeProjectPermission({ projectId })
            .then(() => {
                message.success('申请已提交，7天内有效')
                this.loadProjects()
                this.setState({
                    codexGuideVisible: true,
                    codexGuideScene: VIBE_CODEX_GUIDE_SCENE.APPLICATION_SUBMITTED,
                })
            })
            .catch(() => {})
            .finally(() => this.setState({ actionProjectId: null, actionType: null }))
    }

    getApplicableProjects = () => this.state.projects.filter(project => (
        !this.isTrue(project.isOwner)
        && !this.isTrue(project.hasPermission)
        && !this.isTrue(project.hasPendingRequest)
    ))

    openBatchApply = () => {
        const batchApplyProjects = this.getApplicableProjects()
        this.setState({
            batchApplyVisible: true,
            batchApplyProjects,
            selectedBatchProjectIds: batchApplyProjects.map(project => String(project.id)),
        })
    }

    closeBatchApply = () => {
        if (this.state.batchApplying) return
        this.setState({
            batchApplyVisible: false,
            batchApplyProjects: [],
            selectedBatchProjectIds: [],
        })
    }

    applySelectedProjects = async () => {
        const { selectedBatchProjectIds } = this.state
        if (!selectedBatchProjectIds.length || this.state.batchApplying) return
        this.setState({ batchApplying: true })
        let successCount = 0
        let failureCount = 0
        // 2026-07-21 coder(lq): Submit sequentially so every project keeps the existing validation and notification flow.
        for (const projectId of selectedBatchProjectIds) {
            try {
                await applyVibeProjectPermission({ projectId })
                successCount += 1
            } catch (e) {
                failureCount += 1
            }
        }
        if (!failureCount) {
            message.success(`已提交 ${successCount} 个项目的权限申请`)
        } else if (successCount) {
            message.warning(`已提交 ${successCount} 个申请，${failureCount} 个申请失败`)
        } else {
            message.error('所选项目申请均未提交成功')
        }
        this.setState({
            batchApplying: false,
            batchApplyVisible: false,
            batchApplyProjects: [],
            selectedBatchProjectIds: [],
            codexGuideVisible: successCount > 0,
            codexGuideScene: successCount > 0
                ? VIBE_CODEX_GUIDE_SCENE.APPLICATION_SUBMITTED
                : this.state.codexGuideScene,
        })
        this.loadProjects()
    }

    renderBatchApplyModal = () => {
        const {
            batchApplyVisible,
            batchApplyProjects,
            selectedBatchProjectIds,
            batchApplying,
        } = this.state
        return (
            <Modal
                title="一键申请项目权限"
                visible={batchApplyVisible}
                width={820}
                okText={`确认申请（${selectedBatchProjectIds.length}）`}
                cancelText="取消"
                confirmLoading={batchApplying}
                okButtonProps={{ disabled: !selectedBatchProjectIds.length }}
                onOk={this.applySelectedProjects}
                onCancel={this.closeBatchApply}
                closable={!batchApplying}
                maskClosable={!batchApplying}
                destroyOnClose
            >
                <div style={{ marginBottom: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
                    默认选择尚未申请且没有权限的项目，可取消不需要申请的项目。
                </div>
                <Table
                    rowKey={record => String(record.id)}
                    size="small"
                    pagination={{ pageSize: 10, showSizeChanger: false }}
                    dataSource={batchApplyProjects}
                    locale={{ emptyText: '暂无可以申请的项目' }}
                    rowSelection={{
                        selectedRowKeys: selectedBatchProjectIds,
                        onChange: keys => this.setState({
                            selectedBatchProjectIds: keys.map(key => String(key)),
                        }),
                    }}
                    columns={[
                        {
                            title: '项目',
                            key: 'project',
                            width: 220,
                            render: (_, record) => (
                                <div>
                                    <div>{record.projectName || '-'}</div>
                                    <Text type="secondary" code style={{ fontSize: 12 }}>{record.projectSlug || '-'}</Text>
                                </div>
                            ),
                        },
                        {
                            title: '所有者',
                            key: 'owner',
                            width: 150,
                            render: (_, record) => record.ownerUserNick || record.ownerUserName || record.ownerUserId || '-',
                        },
                        {
                            title: '项目简介',
                            dataIndex: 'description',
                            ellipsis: true,
                            render: value => value || '-',
                        },
                    ]}
                />
            </Modal>
        )
    }

    renderAction = record => {
        const { actionProjectId, actionType } = this.state
        const isLoading = actionProjectId === record.id
        if (this.isTrue(record.isOwner)) {
            return (
                <Button
                    type="link"
                    size="small"
                    loading={isLoading && actionType === 'authorize'}
                    onClick={() => this.openAuthorization(record)}
                >
                    授权
                </Button>
            )
        }
        if (!this.isTrue(record.hasPermission) && this.isTrue(record.hasPendingRequest)) {
            return <Text type="warning">申请中</Text>
        }
        if (!this.isTrue(record.hasPermission)) {
            return (
                <Button
                    type="link"
                    size="small"
                    loading={isLoading && actionType === 'apply'}
                    onClick={() => this.applyProjectPermission(record.id)}
                >
                    申请
                </Button>
            )
        }
        return <Text type="secondary">已有权限</Text>
    }

    renderAiRecommendation = () => {
        const { aiRequirement, aiLoading, aiResult } = this.state
        const recommendations = aiResult?.recommendations || []
        return (
            <Card
                className="profile-vibe-ai-card"
                title={<span><BulbOutlined /> 问 AI 找数据</span>}
                extra={<Text type="secondary">AI 会根据项目的数据说明推荐，最多返回 5 个项目</Text>}
            >
                <Input.Search
                    allowClear
                    size="large"
                    maxLength={500}
                    value={aiRequirement}
                    loading={aiLoading}
                    enterButton="AI 推荐项目"
                    placeholder="例如：我想分析各渠道销售预测、实际订单和目标达成情况"
                    onChange={event => this.setState({ aiRequirement: event.target.value })}
                    onSearch={this.recommendProjects}
                />
                {aiResult ? (
                    <div className="profile-vibe-ai-result-wrap">
                        <Alert
                            showIcon
                            type={recommendations.length > 0 ? 'info' : 'warning'}
                            message={aiResult.answer || (recommendations.length > 0 ? '已为你找到相关项目' : '没有找到匹配项目')}
                        />
                        {recommendations.length > 0 ? (
                            <div className="profile-vibe-ai-result-list">
                                {recommendations.map(record => (
                                    <div className="profile-vibe-ai-result-item" key={record.id}>
                                        <div className="profile-vibe-ai-result-head">
                                            <div>
                                                <Text strong><DatabaseOutlined /> {record.projectName || '-'}</Text>
                                                <Text type="secondary" code className="profile-vibe-ai-project-slug">
                                                    {record.projectSlug || '-'}
                                                </Text>
                                            </div>
                                            <Space>
                                                {this.renderProjectKind(record.projectKind)}
                                                {this.isTrue(record.isOwner) ? <Tag color="blue">我创建的</Tag> : null}
                                                {!this.isTrue(record.isOwner) && this.isTrue(record.hasPermission)
                                                    ? <Tag color="green">已有权限</Tag>
                                                    : null}
                                                {!this.isTrue(record.isOwner) && !this.isTrue(record.hasPermission)
                                                    ? <Tag color="orange">需申请权限</Tag>
                                                    : null}
                                                {this.renderAction(record)}
                                            </Space>
                                        </div>
                                        <div className="profile-vibe-ai-reason">
                                            <Text strong>推荐理由：</Text>{record.reason || '项目数据与需求相关'}
                                        </div>
                                        <div className="profile-vibe-ai-summary">
                                            <Text type="secondary">数据说明：{record.dataSummary || '-'}</Text>
                                        </div>
                                        {Array.isArray(record.matchedData) && record.matchedData.length > 0 ? (
                                            <div className="profile-vibe-ai-matches">
                                                <Text type="secondary">匹配数据：</Text>
                                                {record.matchedData.map(item => <Tag key={item}>{item}</Tag>)}
                                            </div>
                                        ) : null}
                                        <div className="profile-vibe-ai-owner">
                                            <Text type="secondary">
                                                项目所有者：{record.ownerUserNick || record.ownerUserName || record.ownerUserId || '-'}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Card>
        )
    }

    renderAuthorizationModal = () => {
        const {
            authorizationVisible,
            authorizationLoading,
            authorizationLoadError,
            authorizationSaving,
            authorizationProject,
            authorizationUsers,
            selectedAuthorizationUserIds,
            authorizationKeyword,
        } = this.state
        const normalizedKeyword = authorizationKeyword.trim().toLowerCase()
        const filteredAuthorizationUsers = normalizedKeyword
            ? authorizationUsers.filter(item => (
                [item.userNick, item.userName, item.userId, item.mysqlUsername]
                    .some(value => String(value || '').toLowerCase().includes(normalizedKeyword))
            ))
            : authorizationUsers
        const datasetProject = this.isDatasetProject(authorizationProject)
        const policy = getDatasetPolicyMeta(authorizationProject?.sensitivityLevel)
        return (
            <Modal
                title={datasetProject ? '共享数据集授权' : '项目授权'}
                visible={authorizationVisible}
                width={datasetProject ? 920 : 860}
                confirmLoading={authorizationSaving}
                okButtonProps={{ disabled: authorizationLoading || authorizationLoadError }}
                okText="保存授权"
                cancelText="取消"
                onOk={this.saveAuthorization}
                onCancel={() => this.setState({
                    authorizationVisible: false,
                    authorizationProjectId: null,
                    authorizationProject: null,
                    authorizationUsers: [],
                    selectedAuthorizationUserIds: [],
                    authorizationKeyword: '',
                    authorizationLoadError: false,
                })}
                destroyOnClose
            >
                <Spin spinning={authorizationLoading}>
                    {authorizationLoadError ? (
                        <Alert type="error" showIcon message="授权人员加载失败，请关闭弹窗后重试。" style={{ marginBottom: 12 }} />
                    ) : datasetProject ? (
                        <Alert
                            showIcon
                            type={authorizationProject?.sensitivityLevel === 'L3' ? 'error' : 'info'}
                            message={policy.text}
                            description={`${policy.summary} 系统默认授权由敏感级和一级部门自动维护，不能在这里取消；手工勾选的人员获得项目读写权限。`}
                            style={{ marginBottom: 12 }}
                        />
                    ) : (
                        <div style={{ marginBottom: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
                            可选择全部有效用户。未开通 Vibe 空间的用户会在保存时使用个人开通流程自动开通 GitLab 和数据库账号，再授予项目读写权限。
                        </div>
                    )}
                    <Input.Search
                        allowClear
                        value={authorizationKeyword}
                        placeholder="搜索姓名、登录账号或 Vibe 账号"
                        onChange={event => this.setState({ authorizationKeyword: event.target.value })}
                        style={{ width: 320, marginBottom: 12 }}
                    />
                    <Table
                        key={authorizationKeyword}
                        rowKey={record => String(record.userId)}
                        size="small"
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        dataSource={filteredAuthorizationUsers}
                        locale={{ emptyText: '暂无可授权人员' }}
                        rowSelection={{
                            preserveSelectedRowKeys: true,
                            selectedRowKeys: selectedAuthorizationUserIds,
                            onChange: keys => this.setState({
                                selectedAuthorizationUserIds: keys.map(key => String(key)),
                            }),
                            getCheckboxProps: record => ({
                                disabled: this.isTrue(record.isOwner)
                                    || (datasetProject && isSystemManagedDatasetGrant(record, true))
                                    || (!this.isTrue(record.spaceEnabled) && !this.isTrue(record.canProvision)),
                            }),
                        }}
                        columns={[
                            {
                                title: '人员',
                                key: 'user',
                                render: (_, record) => (
                                    <div>
                                        <div>
                                            {record.userNick || record.userName || record.userId || '-'}
                                            {this.isTrue(record.isOwner) ? <Tag color="blue" style={{ marginLeft: 8 }}>项目所有者</Tag> : null}
                                            {datasetProject && isSystemManagedDatasetGrant(record, true)
                                                ? <Tag color="cyan" style={{ marginLeft: 8 }}>系统默认</Tag>
                                                : null}
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {record.userName || record.userId || '-'}
                                        </Text>
                                    </div>
                                ),
                            },
                            {
                                title: 'Vibe空间',
                                key: 'spaceStatus',
                                width: 120,
                                render: (_, record) => {
                                    if (this.isTrue(record.spaceEnabled)) return <Tag color="green">已开通</Tag>
                                    if (record.accountStatus === 'disabled') return <Tag color="red">已停用</Tag>
                                    if (this.isTrue(record.canProvision)) return <Tag color="gold">未开通</Tag>
                                    if (record.accountStatus) return <Tag color="red">账号异常</Tag>
                                    return <Tag>缺少公司邮箱</Tag>
                                },
                            },
                            {
                                title: 'Vibe账号',
                                dataIndex: 'mysqlUsername',
                                width: 160,
                                render: (value, record) => (
                                    value
                                        ? <Text code>{value}</Text>
                                        : <Text type="secondary">{this.isTrue(record.canProvision) ? '授权时创建' : '-'}</Text>
                                ),
                            },
                            {
                                title: '当前权限',
                                dataIndex: 'permissionType',
                                width: 100,
                                render: (value, record) => (
                                    this.isTrue(record.authorized)
                                        ? <Tag color="green">{value === 'readonly' ? '只读' : '读写'}</Tag>
                                        : <Text type="secondary">未授权</Text>
                                ),
                            },
                            ...(datasetProject ? [{
                                title: '授权来源',
                                key: 'grantSource',
                                width: 110,
                                render: (_, record) => (
                                    isSystemManagedDatasetGrant(record, true)
                                        ? <Tag color="cyan">系统策略</Tag>
                                        : (this.isTrue(record.authorized)
                                            ? <Tag color="blue">手工授权</Tag>
                                            : <Text type="secondary">-</Text>)
                                ),
                            }] : []),
                        ]}
                    />
                </Spin>
            </Modal>
        )
    }

    loadPermissionRequests = () => {
        this.setState({ requestLoading: true, requestLoadError: false })
        getMyVibeProjectPermissionRequestList({})
            .then(requests => {
                const permissionRequests = Array.isArray(requests) ? [...requests] : []
                const { focusedRequestId } = this.state
                if (focusedRequestId) {
                    permissionRequests.sort((left, right) => {
                        const leftFocused = String(left.id) === String(focusedRequestId)
                        const rightFocused = String(right.id) === String(focusedRequestId)
                        return leftFocused === rightFocused ? 0 : (leftFocused ? -1 : 1)
                    })
                }
                this.setState({
                    permissionRequests,
                    selectedRequestIds: permissionRequests
                        .filter(request => request.status === 'pending')
                        .map(request => String(request.id)),
                })
            })
            .catch(() => this.setState({ requestLoadError: true }))
            .finally(() => this.setState({ requestLoading: false }))
    }

    openPermissionRequests = () => {
        this.setState({ requestVisible: true, focusedRequestId: null })
        this.loadPermissionRequests()
    }

    closePermissionRequests = () => {
        const { history, location } = this.props
        const search = new URLSearchParams(location?.search)
        search.delete('requestId')
        history.replace({
            pathname: location.pathname,
            search: search.toString(),
        })
        this.setState({
            requestVisible: false,
            focusedRequestId: null,
            selectedRequestIds: [],
        })
    }

    approvePermissionRequest = record => {
        const applicantName = record.applicantUserNick || record.applicantUserName || record.applicantUserId || '申请人'
        Modal.confirm({
            title: `同意 ${applicantName} 的权限申请？`,
            content: `同意后将授予“${record.projectName || '-'}”项目的读写权限。`,
            okText: '同意并授权',
            cancelText: '取消',
            onOk: () => {
                this.setState({ approvingRequestId: record.id })
                return approveVibeProjectPermissionRequest({ requestId: record.id })
                    .then(() => {
                        message.success('已同意并完成授权')
                        this.loadPermissionRequests()
                        this.loadPendingRequestCount()
                        this.loadProjects()
                    })
                    .finally(() => this.setState({ approvingRequestId: null }))
            },
        })
    }

    approveSelectedPermissionRequests = async () => {
        const { selectedRequestIds } = this.state
        if (!selectedRequestIds.length || this.state.batchApproving) return
        this.setState({ batchApproving: true })
        let successCount = 0
        let failureCount = 0
        // 2026-07-21 coder(lq): Process grants sequentially so concurrent updates cannot drop another project permission.
        for (const requestId of selectedRequestIds) {
            try {
                await approveVibeProjectPermissionRequest({ requestId })
                successCount += 1
            } catch (e) {
                failureCount += 1
            }
        }
        if (!failureCount) {
            message.success(`已完成 ${successCount} 个权限授权`)
        } else if (successCount) {
            message.warning(`已完成 ${successCount} 个授权，${failureCount} 个授权失败`)
        } else {
            message.error('所选权限申请均未授权成功')
        }
        this.setState({ batchApproving: false })
        this.loadPermissionRequests()
        this.loadPendingRequestCount()
        this.loadProjects()
    }

    renderPermissionRequestStatus = status => {
        const statusMeta = {
            pending: { text: '待授权', color: 'orange' },
            approved: { text: '已同意', color: 'green' },
            expired: { text: '已过期', color: 'default' },
        }
        const meta = statusMeta[(status || '').toLowerCase()] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderPermissionRequestModal = () => {
        const {
            requestVisible,
            requestLoading,
            requestLoadError,
            permissionRequests,
            approvingRequestId,
            selectedRequestIds,
            batchApproving,
            focusedRequestId,
        } = this.state
        return (
            <Modal
                title="谁申请了我的项目"
                visible={requestVisible}
                width={1000}
                footer={[
                    <Button key="close" onClick={this.closePermissionRequests} disabled={batchApproving}>关闭</Button>,
                    <Button
                        key="batchApprove"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={batchApproving}
                        disabled={!selectedRequestIds.length}
                        onClick={this.approveSelectedPermissionRequests}
                    >
                        一键授权（{selectedRequestIds.length}）
                    </Button>,
                ]}
                onCancel={this.closePermissionRequests}
                closable={!batchApproving}
                maskClosable={!batchApproving}
                destroyOnClose
            >
                {requestLoadError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="权限申请加载失败"
                        action={<Button size="small" onClick={this.loadPermissionRequests}>重试</Button>}
                        style={{ marginBottom: 12 }}
                    />
                ) : null}
                <Spin spinning={requestLoading}>
                    <Table
                        rowKey={record => String(record.id)}
                        size="small"
                        dataSource={permissionRequests}
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        locale={{ emptyText: '暂时没有人申请你的项目' }}
                        scroll={{ x: 900 }}
                        rowSelection={{
                            selectedRowKeys: selectedRequestIds,
                            onChange: keys => this.setState({
                                selectedRequestIds: keys.map(key => String(key)),
                            }),
                            getCheckboxProps: record => ({ disabled: record.status !== 'pending' }),
                        }}
                        rowClassName={record => (
                            focusedRequestId && String(record.id) === String(focusedRequestId)
                                ? 'profile-vibe-permission-request-focused'
                                : ''
                        )}
                        columns={[
                            {
                                title: '申请人',
                                key: 'applicant',
                                width: 180,
                                render: (_, record) => (
                                    <div>
                                        <div>{record.applicantUserNick || record.applicantUserName || record.applicantUserId || '-'}</div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {record.applicantUserName || record.applicantUserId || '-'}
                                        </Text>
                                    </div>
                                ),
                            },
                            {
                                title: '申请项目',
                                key: 'project',
                                width: 200,
                                render: (_, record) => (
                                    <div>
                                        <div>{record.projectName || '-'}</div>
                                        <Text type="secondary" code style={{ fontSize: 12 }}>{record.projectSlug || '-'}</Text>
                                    </div>
                                ),
                            },
                            {
                                title: '申请时间',
                                dataIndex: 'createTime',
                                width: 150,
                                render: this.formatDate,
                            },
                            {
                                title: '有效期至',
                                dataIndex: 'expireTime',
                                width: 150,
                                render: this.formatDate,
                            },
                            {
                                title: '状态',
                                dataIndex: 'status',
                                width: 90,
                                render: this.renderPermissionRequestStatus,
                            },
                            {
                                title: '操作',
                                key: 'action',
                                fixed: 'right',
                                width: 100,
                                render: (_, record) => record.status === 'pending' ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        loading={String(approvingRequestId) === String(record.id)}
                                        disabled={batchApproving}
                                        onClick={() => this.approvePermissionRequest(record)}
                                    >
                                        同意
                                    </Button>
                                ) : <Text type="secondary">-</Text>,
                            },
                        ]}
                    />
                </Spin>
            </Modal>
        )
    }

    getColumns = () => ([
        {
            title: '项目名称',
            dataIndex: 'projectName',
            key: 'projectName',
            width: 220,
            render: (value, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{this.isDatasetProject(record) ? <DatabaseOutlined /> : <CloudServerOutlined />} {value || '-'}</Text>
                    <Text type="secondary" code>{record.projectSlug || '-'}</Text>
                </Space>
            ),
        },
        {
            title: '登记人',
            key: 'owner',
            width: 140,
            render: (_, record) => record.ownerUserNick || record.ownerUserName || record.ownerUserId || '-',
        },
        {
            title: '数据 Owner',
            dataIndex: 'dataOwner',
            key: 'dataOwner',
            width: 180,
            ellipsis: true,
            render: (value, record) => this.isDatasetProject(record) ? (value || '-') : <Text type="secondary">-</Text>,
        },
        {
            title: '当前位置',
            dataIndex: 'dataLocation',
            key: 'dataLocation',
            width: 260,
            ellipsis: true,
            render: (value, record) => this.isDatasetProject(record) ? (value || '-') : <Text type="secondary">-</Text>,
        },
        {
            title: '项目简介',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: value => value || '-',
        },
        {
            title: '数据说明',
            dataIndex: 'dataSummary',
            key: 'dataSummary',
            width: 360,
            ellipsis: true,
            render: value => value || <Text type="secondary">暂无数据说明</Text>,
        },
        {
            title: '项目用途',
            dataIndex: 'projectKind',
            key: 'projectKind',
            width: 110,
            render: this.renderProjectKind,
        },
        {
            title: '运行方式',
            dataIndex: 'deploymentMode',
            key: 'deploymentMode',
            width: 120,
            render: this.renderDeploymentMode,
        },
        {
            title: '数据权限',
            key: 'datasetPolicy',
            width: 180,
            render: (_, record) => {
                if (!this.isDatasetProject(record)) return <Text type="secondary">-</Text>
                const policy = getDatasetPolicyMeta(record.sensitivityLevel)
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color={policy.color}>{record.sensitivityLevel || 'L1'}</Tag>
                        <Text type="secondary">{record.ownerDepartmentName || '未设置一级部门'}</Text>
                    </Space>
                )
            },
        },
        {
            title: '更新频率',
            key: 'updateFrequency',
            width: 130,
            render: (_, record) => this.isDatasetProject(record)
                ? this.getUpdateFrequencyText(record)
                : <Text type="secondary">-</Text>,
        },
        {
            title: '主体',
            dataIndex: 'businessSubject',
            key: 'businessSubject',
            width: 90,
            render: this.renderBusinessSubject,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: this.renderStatus,
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 160,
            render: this.formatDate,
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 100,
            render: (_, record) => this.renderAction(record),
        },
    ])

    render() {
        const {
            loading,
            loadError,
            projects,
            projectFilter,
            projectKindFilter,
            pendingRequestCount,
            requestCountLoading,
        } = this.state
        const kindFilteredProjects = this.filterProjects(projects, 'all', projectKindFilter)
        const ownedProjectCount = this.filterProjects(projects, 'owned', projectKindFilter).length
        const permittedProjectCount = this.filterProjects(projects, 'permitted', projectKindFilter).length
        const filteredProjects = this.filterProjects(projects, projectFilter, projectKindFilter)
        if (loadError) {
            return (
                <Result
                    status="error"
                    title="Vibe 项目列表加载失败"
                    subTitle="无法获取全部 Vibe 项目，请稍后重试"
                    extra={<Button type="primary" onClick={this.loadProjects}>重试</Button>}
                />
            )
        }

        return (
            <div className="profile-vibe-wrap profile-vibe-project-list-wrap">
                {this.renderAiRecommendation()}
                <div className="profile-vibe-toolbar">
                    <div className="profile-market-toolbar-title">Vibe 项目列表</div>
                    <Space wrap>
                        <Space size={6}>
                            <Text type="secondary">项目用途</Text>
                            <Select
                                value={projectKindFilter}
                                style={{ width: 132 }}
                                onChange={value => this.setState({ projectKindFilter: value })}
                            >
                                <Select.Option value="dataset">共享数据集</Select.Option>
                                <Select.Option value="application">业务应用</Select.Option>
                                <Select.Option value="all">全部用途</Select.Option>
                            </Select>
                        </Space>
                        <Radio.Group
                            value={projectFilter}
                            buttonStyle="solid"
                            onChange={event => this.setState({ projectFilter: event.target.value })}
                        >
                            <Radio.Button value="all">所有项目（{kindFilteredProjects.length}）</Radio.Button>
                            <Radio.Button value="owned">我创建的项目（{ownedProjectCount}）</Radio.Button>
                            <Radio.Button value="permitted">我有权限的别人项目（{permittedProjectCount}）</Radio.Button>
                        </Radio.Group>
                        <Button
                            type="primary"
                            danger
                            icon={<DatabaseOutlined />}
                            onClick={() => this.setState({
                                codexGuideVisible: true,
                                codexGuideScene: VIBE_CODEX_GUIDE_SCENE.NORMAL,
                            })}
                        >
                            申请和授权有啥用？
                        </Button>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={this.openBatchApply}
                        >
                            一键申请
                        </Button>
                        <Badge count={pendingRequestCount} overflowCount={99} offset={[-2, 2]}>
                            <Button
                                icon={<TeamOutlined />}
                                loading={requestCountLoading}
                                onClick={this.openPermissionRequests}
                            >
                                谁申请了我的项目
                            </Button>
                        </Badge>
                        <Button icon={<ReloadOutlined />} onClick={this.refreshPage}>刷新</Button>
                    </Space>
                </div>
                <Spin spinning={loading}>
                    {filteredProjects.length > 0 ? (
                        <Table
                            key={`${projectFilter}-${projectKindFilter}`}
                            rowKey="id"
                            columns={this.getColumns()}
                            dataSource={filteredProjects}
                            pagination={{ pageSize: 20, showSizeChanger: false }}
                            scroll={{ x: 2350 }}
                        />
                    ) : (
                        <Empty description={this.getEmptyDescription(projectFilter, projectKindFilter)} />
                    )}
                </Spin>
                {this.renderBatchApplyModal()}
                {this.renderAuthorizationModal()}
                {this.renderPermissionRequestModal()}
                <VibeCodexGuideDialog
                    visible={this.state.codexGuideVisible}
                    scene={this.state.codexGuideScene}
                    onClose={() => this.setState({
                        codexGuideVisible: false,
                        codexGuideScene: VIBE_CODEX_GUIDE_SCENE.NORMAL,
                    })}
                />
            </div>
        )
    }
}

export default withRouter(VibeProjectList)
